import React, { ComponentProps } from 'react';

import { Flags } from 'app/developer-v3/platformSchema/flags';
import { useIntegrationVersionConfig } from 'app/developer-v3/hooks';
import LoadingBlock from 'app/common/components/LoadingBlock';

import Settings from './Settings';

type Props = {
  flags: Flags;
  integrationId: number;
  integrationVersion: string;
  isGUIIntegration: boolean;
  saveFlags: (flags: Flags) => Promise<void>;
} & ComponentProps<typeof Settings>;

// TODO: Remove either this wrapper or its parent Container comonent once all
// settings are migrated away from Entites CliApp and CliAppDefinition. Ideally,
// we only use hooks useQueryImplementation and useQueryIntegrationVersionConfig
// to fetch and save settings. That way we can remove the Entities layer
// entirely.
//
// This wrapper exists to bridge the gap between the two methods of handling
// settings. Its parent Container component fetches the definition flags by
// Entities CliApp and CliAPpDefinition. Meanwhile, this wrapper fetches the
// version config by using the useIntegrationVersionConfig hook. The
// presentational component Settings should be agnostic of where the data comes
// from. So this wrapper passes down the necessary data and handles saving
// both the definition flags and the version config when the user saves the
// settings.
function Wrapper({
  flags,
  integrationId,
  integrationVersion,
  isGUIIntegration,
  isSaving: isSavingFlags,
  saveFlags,
  ...props
}: Props) {
  const {
    integrationVersionConfig,
    isLoading,
    isUpdating: isUpdatingIntegrationVersionConfig,
    update: updateIntegrationVersionConfig,
  } = useIntegrationVersionConfig({
    integrationId,
    integrationVersion,
    optionsUseQueryIntegrationVersionConfig: {},
    // We provide a no-op onSuccess to prevent two notifications from
    // showing up for UI integrations since saving definition flags also
    // triggers a notification. We don't save definition flags for CLI
    // integrations because they're read-only in the UI. That prevents us
    // from having double notifications for CLI integrations.
    optionsUseMutationUpdateIntegrationVersionConfig: isGUIIntegration
      ? { onSuccess: () => undefined }
      : undefined,
  });

  return isLoading ? (
    <LoadingBlock />
  ) : (
    <Settings
      {...props}
      isGUIIntegration={isGUIIntegration}
      isInitialSkipHttpPatch={flags.skipHttpPatch}
      isInitialSkipThrowForStatus={flags.skipThrowForStatus}
      isInitialStaticIPEnabled={
        integrationVersionConfig?.isStaticIPEnabled ?? false
      }
      isSaving={isSavingFlags || isUpdatingIntegrationVersionConfig}
      onSave={async ({
        isStaticIPEnabled,
        isSkipHttpPatch,
        isSkipThrowForStatus,
      }) => {
        await Promise.allSettled([
          updateIntegrationVersionConfig({
            ...(integrationVersionConfig ?? { isStaticIPEnabled: false }),
            isStaticIPEnabled,
          }),
          ...(isGUIIntegration
            ? [
                saveFlags({
                  skipHttpPatch: isSkipHttpPatch,
                  skipThrowForStatus: isSkipThrowForStatus,
                }),
              ]
            : []),
        ]);
      }}
    />
  );
}

export default Wrapper;
